// src/prototype/pages/EAPrintableExecutiveOnePagerPage.tsx
// Executive Brief — Leadership Brief
// CI pass: renamed from Executive One-Pager, summary strip updated,
// all sections tightened for executive readability.
// Shell architecture, route, and print styling unchanged.

import React from 'react';
import { DEMO_CLIENT } from '../config/demoClient';
import { demoMonthYear } from '../config/demoDates';
import {
  EAUseCaseArtifactShell,
  UACallout,
  type SummaryMetric,
  type ArtifactSection,
} from '../components/ea_reports/use_case/EAUseCaseArtifactShell';
import { CANON } from '../roi/roiCanon';
import { TOTAL_PORTFOLIO_SIZE } from '../data/decisionBrief';

// ─── Summary Strip ────────────────────────────────────────────────────────────
//
// Financial figures come from the ROI engine (src/prototype/roi). The investment
// line states the resolved canon — implementation plus operating, totalled — in
// place of the former "$2.1M – $3.4M total implementation cost", which
// contradicted the Execute Command Center's "$1.2M".

const SUMMARY_METRICS: SummaryMetric[] = [
  { label: 'Strategic Priority',     value: 'High',                 sub: 'enterprise initiative'     },
  { label: 'Required Investment',    value: CANON.totalCost3Yr,     sub: 'total 3-year cost'         },
  { label: 'Estimated Annual Value', value: CANON.annualValueRange, accent: true, sub: 'conservative–aggressive span' },
  { label: 'Payback Period',         value: CANON.basePayback,      accent: true, sub: 'base case'     },
  { label: 'Recommendation',         value: 'Recommended to Advance', sub: 'first-wave portfolio'      },
];

// ─── Sections ─────────────────────────────────────────────────────────────────

const SECTIONS: ArtifactSection[] = [
  {
    title: 'Initiative Overview',
    content: (
      <>
        <p style={{ fontSize: '8pt', color: '#717182', fontStyle: 'italic', marginBottom: '10px' }}>
          This brief was generated by the DVA (Data Value Accelerator), part of the Strategic Intelligence
          Platform, and reflects the platform's composite evaluation of this initiative.
          Full initiative evaluation — including financial modeling, risk assessment, and implementation feasibility — is available in the Use Case Profile.
        </p>
        <p>
          Care Coordination and Transition Management applies predictive risk stratification
          at discharge to trigger targeted post-acute interventions for high-risk patients.
          It directly addresses avoidable readmissions and fragmented care transitions —
          transforming reactive follow-up into a proactive, data-driven care management workflow.
        </p>
      </>
    ),
  },
  {
    title: 'Strategic Rationale',
    content: (
      <>
        <p>
          Care transitions are among the most significant sources of avoidable cost in
          value-based care. Patients discharged without structured risk assessment are
          substantially more likely to be readmitted within 30 days — driving costs that
          fall directly on the health system under bundled payment and shared savings contracts.
        </p>
        <p>
          This initiative reduces avoidable utilization, improves care coordination across
          high-risk populations, and strengthens payer contract performance tied to readmission
          quality metrics — directly aligned with {DEMO_CLIENT.name}'s value-based care strategy.
        </p>
        <UACallout>
          Ranked #1 of {TOTAL_PORTFOLIO_SIZE} fully evaluated initiatives — 99th percentile on
          composite business value and implementation feasibility
        </UACallout>
      </>
    ),
  },
  {
    title: 'Financial Headline',
    content: (
      <p>
        The initiative is projected to deliver <strong>{CANON.annualValueRange} in estimated annual
        value</strong> with a <strong>{CANON.basePayback} payback period</strong> at base case
        assumptions, and <strong>{CANON.baseNetValue3Yr} in net value over three years</strong>
        (benefits discounted at {CANON.discountRate}, net of the {CANON.totalCostLabel}).
        The primary value driver is reduced readmissions and improved care coordination
        efficiency across high-risk transition populations. Model confidence is{' '}
        {CANON.confidencePct}; the full model and its definitions are in the CFO ROI Summary.
      </p>
    ),
  },
  {
    title: 'Execution Snapshot',
    content: (
      <>
        <p>
          Implementation complexity is rated <strong>Low</strong> relative to the current
          initiative portfolio. Required data infrastructure — patient encounter history,
          discharge records, and risk stratification inputs — is available within existing
          clinical and operational systems.
        </p>
        <p>
          Initial implementation should focus on integrating risk-based transition workflows
          into care management and discharge planning operations. Key readiness dependency:
          alignment across care management, clinical informatics, and discharge planning on
          workflow adoption and escalation protocols.
        </p>
      </>
    ),
  },
  {
    title: 'Recommendation',
    content: (
      <div style={{ background: '#18365E', borderRadius: '8px', padding: '18px 22px' }}>
        <div style={{
          fontSize: '7pt', fontWeight: 700, letterSpacing: '0.12em',
          textTransform: 'uppercase', color: '#FF6E4C', marginBottom: '8px',
        }}>
          Executive Recommendation
        </div>
        <p style={{ fontSize: '10pt', fontWeight: 700, color: '#ffffff', lineHeight: 1.55, margin: 0 }}>
          Care Coordination and Transition Management is recommended to advance to the execution planning phase.
        </p>
        <p style={{
          fontSize: '9pt', fontWeight: 400, color: 'rgba(255,255,255,0.8)',
          lineHeight: 1.65, margin: '10px 0 0',
        }}>
          This initiative belongs in the first execution wave. The financial case is compelling:
          {CANON.annualValueRange} in annual value against {CANON.investmentSchedule}
          ({CANON.totalCostLabel}), recovering in {CANON.basePayback}. Implementation risk is low
          and the operational infrastructure is in place.
          Leadership review and Phase 1 initiation are recommended as near-term next steps.
        </p>
      </div>
    ),
  },
];

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function EAPrintableExecutiveOnePagerPage() {
  return (
    <EAUseCaseArtifactShell
      title="Executive Brief"
      subtitle="Leadership Brief"
      clientName={DEMO_CLIENT.name}
      useCaseName="Care Coordination and Transition Management"
      reportDate={demoMonthYear()}
      summaryMetrics={SUMMARY_METRICS}
      sections={SECTIONS}
      manualPrint
      printLabel="Executive Brief"
    />
  );
}